/* CHROMATIC ORB — the 2024 "leap" (bounce) detector, pure logic.
 *
 * 2024 PHB (Chromatic Orb, level 1 Evocation): on a hit the target takes Nd8 of a
 * chosen type (3d8 at level 1, +1d8 per slot level above 1). "If you roll the same
 * number on two or more of the d8s, the orb leaps to a different creature of your
 * choice within 30 feet of the target" — a fresh attack roll and damage roll against
 * the new target. The orb leaps at most a capped number of times and never to a
 * creature it has already targeted this casting.
 *
 * The LEAP CAP is the one genuinely contested number: aidedd (matching PHB p.249)
 * reads it as the level of the slot expended — so a 1st-level cast can leap once —
 * while some community readings say no leap at level 1. Because the sources disagree,
 * the cap is a PARAMETER here, not a constant: the caller supplies it from the slot
 * the DM actually spent, so the table's ruling lives at the call site, not buried in
 * a detector. The TRIGGER (two or more equal d8s) is not contested and is fixed.
 *
 * Deliberately ONLY the dice -> leap decision: no chat parsing, no marker placement.
 * The connector chat lane that feeds it the real d8 faces, and the reticle it spawns
 * on a leap, both need a live Roll20 damage card to build against — so they wait. */

/** The leap goes to a new target within this many feet of the struck target (2024). */
export const CHROMATIC_ORB_LEAP_RANGE_FT = 30;

/** The leap TRIGGER: two or more of the damage dice show the same number. An empty
 *  or single-die roll never triggers; a face that failed to parse (non-finite) is
 *  skipped rather than counted as a match. */
export function diceRepeat(dice: number[]): boolean {
  const seen = new Set<number>();
  for (const d of dice) {
    if (!Number.isFinite(d)) continue;
    if (seen.has(d)) return true;
    seen.add(d);
  }
  return false;
}

export interface LeapState {
  /** how many times the orb has already leapt this casting */
  leapsSoFar: number;
  /** the cap — RAW, the level of the slot expended (supplied by the caller) */
  maxLeaps: number;
  /** creature ids the orb has already targeted this casting; it can't leap back */
  targetedIds: string[];
}

/** Whether the orb may leap after THIS damage roll: the dice triggered a leap AND
 *  the cap has not been reached. Target choice is the DM's; "not already targeted"
 *  is enforced when they pick, via eligibleLeapTargets. */
export function canLeap(dice: number[], state: LeapState): boolean {
  return diceRepeat(dice) && state.leapsSoFar < Math.max(0, state.maxLeaps);
}

/** The creatures the orb may leap TO: the in-range candidates the caller supplies,
 *  minus any the orb has already targeted this casting. Pure set logic — range is
 *  the caller's to filter against the map; this only removes the already-hit ones. */
export function eligibleLeapTargets(candidateIds: string[], state: LeapState): string[] {
  const hit = new Set(state.targetedIds);
  return candidateIds.filter((id) => !hit.has(id));
}
